/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';

import { object } from 'prop-types';
import { compose } from 'recompose';
import { useDispatch } from 'react-redux';
import {
    Dialog, DialogContent, withStyles, DialogTitle, Grid,
    Slide,
} from '@material-ui/core';
import Cookies from 'universal-cookie';
import CloseRounded from '@material-ui/icons/CloseRounded';
import NativeSigninUIComponent from './NativeSigninUIComponent';
import { emitCustomTrackingEvent } from '../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import { setNativeLoginPromptRefetchTimestamp, setNativeLoginPromptSFResponse } from '../../../../state/ducks/App/App-Actions';
import { fetchUserEmailbyBrowserUUID } from '../../../../apis/account-apis/fetchUserEmailbyBrowserUUID';

const styles = (theme) => ({
    dialogContainer: {
        zIndex: '1200 !important',
        [theme.breakpoints.up(768)]: {
            display: 'inline-block',
            height: 'fit-content',
            width: 'fit-content',
            top: '50% !important',
            left: 'calc(100vw - 410px) !important',
            transform: 'translateY(-50%)',
            // this is safari specific to fix scrolling issue with calendar delivery date options
            '@supports (-webkit-appearance:none)': {
                height: 'auto',
            },
        },
        '@media (min-width: 500px) and (max-width: 767px)': {
            display: 'inline-block',
            height: 'fit-content',
            width: 'fit-content',
            top: '50% !important',
            left: '50%!important',
            transform: 'translate(-50%, -50%)',
        },
        '& .MuiDialog-scrollPaper': {
            '@media (min-width: 768px)': {
                justifyContent: 'end !important',
                marginRight: '10px !important',
            },
            [theme.breakpoints.down(500)]: {
                alignItems: 'end !important',
            },
            '& .MuiPaper-root': {
                borderRadius: '10px',
                margin: '0px !important',
                [theme.breakpoints.down(500)]: {
                    margin: '10px !important',
                    width: '100%',
                    marginBottom: '-10px !important',
                },
                '& .MuiDialogContent-root': {
                    padding: '0px !important',
                    '& .MuiPaper-root': {
                        boxShadow: 'none !important',
                    },
                },
            },
            '& h1': {
                fontSize: '20px !important',
                lineHeight: '30px !important',
            },
            '& .privacyPolicyBlock': {
                fontSize: '10px',
                lineHeight: '15px',
                '& a': {
                    margin: '0px 4px 0px 4px',
                },
            },
            '& .login': {
                '& .MuiFormControl-root': {
                    marginTop: '14px',
                },
            },
            '& .mainContainer': {
                backgroundColor: 'white !important',
                '& .MuiCardContent-root': {
                    padding: '0px 30px !important',
                    [theme.breakpoints.down(500)]: {
                        paddingBottom: '30px !important',
                    },
                },
            },
        },
    },
    closeIconWrapper: {
        fill: 'rgba(0, 0, 0, 0.6)',
        position: 'absolute',
        right: '10px',
        top: '10px',
        cursor: 'pointer',
    },
});

// eslint-disable-next-line prefer-arrow-callback, react/jsx-props-no-spreading
const Transition = React.forwardRef(function Transition(props, ref) { return <Slide direction={window.screen.width < 500 ? 'up' : 'left'} ref={ref} {...props} />; });
const NativeSignInPrompt = ({ classes, salesForceData, page }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [emailId, setEmailId] = useState('');
    const dispatch = useDispatch();

    useEffect(() => {
        const userEmailAPICall = async () => {
            let userEmailID = await fetchUserEmailbyBrowserUUID();
            if (!userEmailID) {
                const cookies = new Cookies();
                userEmailID = cookies.get('oneClickEmail');
            }
            setEmailId(userEmailID);
        };
        userEmailAPICall();
        setTimeout(() => {
            const userGroup = salesForceData?.campaign?.campaignResponses?.[0]?.payload?.userGroup;
            const nativeLoginPromptEventFired = /^true$/i.test(window?.sessionStorage?.getItem('nativeLoginPromptEventFired'));
            const nativeLoginPromptDisplayed = /^true$/i.test(window?.sessionStorage?.getItem('nativeLoginPromptDisplayed'));
            const currentTime = new Date().getTime();
            if (userGroup?.toLowerCase?.() === 'test' && nativeLoginPromptDisplayed) setIsOpen(true);
            if (!nativeLoginPromptEventFired && userGroup) {
                dispatch(emitCustomTrackingEvent({
                    action: 'Impression',
                    salesforceResponse: salesForceData,
                    page,
                    eventSubType: 'campaign-tracking',
                }));
                window.sessionStorage.setItem('nativeLoginPromptEventFired', true);
                window.sessionStorage.setItem('nativeLoginPromptDisplayed', true);
            }
            dispatch(setNativeLoginPromptRefetchTimestamp(currentTime));
            dispatch(setNativeLoginPromptSFResponse(salesForceData));
        }, 500);
    }, []);

    const dialogEntered = () => {
        if (typeof window !== 'undefined' && document.body.contains(document.getElementById('native-sign-in-prompt'))) {
            document?.getElementById('root')?.removeAttribute('aria-hidden');
            document?.getElementsByTagName('body')?.[0]?.removeAttribute('style');
        }
        window.sessionStorage.setItem('nativeLoginPromptDisplayed', true);
    };

    return (
        <>
            <Dialog
                id="native-sign-in-prompt"
                open={isOpen}
                hideBackdrop
                data-testid="native-sign-in-prompt"
                className={classes.dialogContainer}
                onEntered={dialogEntered}
                TransitionComponent={Transition}
                transitionDuration={{
                    appear: 1000,
                    enter: 1000,
                    exit: 200,
                }}
                disableEnforceFocus
                disableBackdropClick
                disableScrollLock
            >
                <DialogTitle className={classes.dialogTitle}>
                    <Grid item container>
                        <Grid
                            item
                            className={classes.closeIconWrapper}
                            onClick={() => {
                                dispatch(emitCustomTrackingEvent({
                                    eventType: 'custom',
                                    eventSubType: 'campaign-tracking',
                                    action: 'Dismissal',
                                    category: 'Login Prompt',
                                    salesforceResponse: salesForceData,
                                }));
                                setIsOpen(false);
                                window.sessionStorage.setItem('nativeLoginPromptDisplayed', false);
                            }}
                            id="native-sign-in-prompt-close-button"
                            data-testid="native-sign-in-prompt-close-button"
                        >
                            <CloseRounded alt="close-btn-img" className={classes.closeIcon} />
                        </Grid>
                    </Grid>
                </DialogTitle>
                <DialogContent>
                    <NativeSigninUIComponent isPrompt emailId={emailId} salesForceData={salesForceData} />
                </DialogContent>
            </Dialog>
        </>
    );
};

NativeSignInPrompt.propTypes = {
    classes: object.isRequired,
    salesForceData: object.isRequired,
    page: object.isRequired,
};

const enhance = compose(
    withStyles(styles),
);

export default enhance(NativeSignInPrompt);
